import React, { useState, useEffect } from "react";
import { ArrowBigUpDash, FolderKanban, Check, X } from "lucide-react";
import { getOrderIndex } from "./AnswerEvaluateEngine";

// Utility: Shuffle items
const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

export default function DragOrder({ question, element, onAttemptUpdate }) {
  const [items, setItems] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [score, setScore] = useState(null);

  useEffect(() => {
    if (element?.dropdownValues) {
      const parsed = JSON.parse(element.dropdownValues);
      const list = parsed.map((obj) => obj?.optionTitle);
      setItems(shuffle(list)); // shuffled list on mount
    }
  }, [element]);

  const handleDragStart = (_, index) => setDraggedIndex(index);

  const handleDrop = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null) return;

    const reordered = [...items];
    const [draggedItem] = reordered.splice(draggedIndex, 1);
    reordered.splice(index, 0, draggedItem);

    setItems(reordered);
    setDraggedIndex(null);
  };
const [dragOverIndex, setDragOverIndex] = useState(null);

const handleDragOver = (e, index) => {
  e.preventDefault();
  setDragOverIndex(index);
};

const handleDragEnd = () => {
  setDragOverIndex(null);
};
  const resetOrder = () => {
    setScore(null);
    const parsed = JSON.parse(element.dropdownValues)
      .map((o) => o?.optionTitle);
    setItems(shuffle(parsed));
  };

  const checkOrder = () => {
    const parsed = JSON.parse(element.dropdownValues);
    const correctOrder = parsed.map((o) => o.optionTitle);

    let correctCount = 0;
    items.forEach((item, i) => {
      if (item === correctOrder[i]) correctCount++;
    });

    const totalMarks = parsed.reduce(
      (sum, item) => sum + (item?.partialMarksChoice || 1),
      0
    );

    const gainedMarks = parsed.reduce((sum, item, idx) => {
      return items[idx] === item.optionTitle
        ? sum + (item?.partialMarksChoice || 1)
        : sum;
    }, 0);

    const scoreObj = {
      correctCount,
      totalMarks,
      gainedMarks,
    };

    setScore(scoreObj);

    onAttemptUpdate({
      questionId: question?.ID,
      queType: "Drag Ordering",
      score: gainedMarks,
      rawScore: scoreObj,
      status:
        correctCount === 0
          ? "wrong"
          : correctCount === items.length
          ? "correct"
          : "partially-correct",
      isCorrect: correctCount === items.length,
      userOrder: items,
      userOrderIndexes: items.map((x) =>
        getOrderIndex(x, element.dropdownValues)
      ),
    });
  };

  if (!items.length) return null;

  const correctOrder = JSON.parse(element.dropdownValues).map(
    (o) => o.optionTitle
  );

  return (
    <div style={{ fontFamily: "sans-serif" }}>
      <h3>Arrange in Correct Order</h3>

      {/* Drop Zones + Items */}
      <div>
        {items.map((item, index) => {
          const correct = item === correctOrder[index];
    const showDropZone = dragOverIndex === index;

          return (
            <React.Fragment key={item}>
              {/* Drop Zone above item — shows only on dragOver */}
        {showDropZone && (
          <div
            onDragOver={(e) => handleDragOver(e, index)}
            onDrop={(e) => handleDrop(e, index)}
            style={{
              height: "30px",fontSize:"12px",
              margin: "6px 0",display:"flex",justifyContent:"center",alignItems:"center",color:"#555",
              borderRadius: "6px",
              background: "rgba(144,202,249,0.2)",
              transition: "0.2s",
            }}
          >Drop Here</div>
        )}

       {/* Draggable item */}
              <div
                draggable
          onDragStart={(e) => handleDragStart(e, index)}
          onDragOver={(e) => handleDragOver(e, index)}
          onDragEnd={handleDragEnd}
          style={{
                  position: "relative",
                  margin: "5px 0 25px",
                  padding: "10px 20px",
                  minHeight: "30px",
                  background: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderRadius: "6px",
                  cursor: "grab",
                  boxShadow: "0 7px 20px rgba(0,0,0,0.05)",
                  border: score
                    ? correct
                      ? "2px solid #c8f7c5"
                      : "2px solid #f7c5c5"
                    : "2px solid rgba(235,236,237,1)",
                }}
              >
                {item}

                {/* Badge - index or correctPosition */}
                <div
                  style={{
                    position: "absolute",
                    left: "-12px",
                    top: "35%",
                    width: "22px",
                    height: "22px",
                    borderRadius: "50%",
                    background: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "13px",
                    border: score
                      ? correct
                        ? "2px solid #c8f7c5"
                        : "2px solid #f7c5c5"
                      : "2px solid rgba(235,236,237,1)",
                  }}
                >
                  {score
                    ? getOrderIndex(item, element.dropdownValues) + 1
                    : index + 1}
                </div>

                {/* Marks or Wrong Label */}
                {score && (
                  <div
                    style={{
                      position: "absolute",
                      right: "-10px",
                      top: "-15px",
                      padding: "3px 6px",
                      borderRadius: "6px",
                      background: "#fff",
                      border: correct
                        ? "2px solid #c8f7c5"
                        : "2px solid #f7c5c5",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    {correct ? (
                      <>
                        <Check color="#61e35f" size={14} /> Correct
                      </>
                    ) : (
                      <>
                        <X color="#f06c6c" size={14} /> Wrong
                      </>
                    )}
                  </div>
                )}
              </div>
      </React.Fragment>
    );
  })}

  {/* Final Drop Zone — Shows only when dragging over last */}
  {dragOverIndex === items.length - 1 && (
    <div
      onDragOver={(e) => handleDragOver(e, items.length)}
      onDrop={(e) => handleDrop(e, items.length)}
      style={{
        height: "30px",display:"flex",justifyContent:"center",alignItems:"center",color:"#555",
        margin: "6px 0",fontSize:"12px",
        borderRadius: "6px",
        background: "rgba(144,202,249,0.8)",
      }}
      >Drop Here</div>
  )}
</div>

      {/* Buttons */}
      <div style={{ display: "flex", gap: "10px", marginTop: "15px" }}>
        <button
          onClick={resetOrder}
          style={{
            flex: 1,
            padding: "8px",
            borderRadius: "6px",
            border: "2px solid rgba(144,202,249,1)",
            background: "rgba(144,202,249,1)",
            color: "#fff",
          }}
        >
          <FolderKanban size={18} /> Reset & Shuffle
        </button>

        <button
          onClick={checkOrder}
          style={{
            flex: 1,
            padding: "8px",
            borderRadius: "6px",
            border: "2px solid rgba(144,202,249,1)",
            background: "#fff",
            color: "#222",
          }}
        >
          <ArrowBigUpDash size={18} color="rgba(144,202,249,1)" /> Check
          Result
        </button>
      </div>

      {score && (
        <div
          style={{
            marginTop: "15px",
            padding: "10px 15px",
            background: "#eee",
            borderRadius: "6px",
            fontWeight: 600,
          }}
        >
          🎯 Correct: {score.correctCount}/{items.length} <br />
          💯 Marks: {score.gainedMarks} / {score.totalMarks}
        </div>
      )}
    </div>
  );
}
